import * as Sentry from '@sentry/vue'
import PricingApi from '@/api/pricing'
import PromoApi from '@/api/promo'
import utils from '@/util'
import router from '@/router'
import InvoiceApi from '@/api/invoice'
import mixpanelUtils from '@/utils/mixpanel.utils'
import { MONTHLY_YEARLY_UPGRADE_PLAN_MAP } from '@/constant/monthlyToYearlyUpgrade'

const DEFAULT_TOAST_ERROR_MESSAGE = 'Something went wrong, please check your connection or contact us for support'

const createCheckoutSessionAction = async ({ commit, dispatch, rootGetters }, payload) => {
  try {
    commit('isCreatingCheckoutSession', true)

    const useV2Pricing = rootGetters['featureToggles/canUseNewBulkPricing']
    const apiMethod = useV2Pricing ?
      PricingApi.createStripeCheckoutSessionV2 :
      PricingApi.createStripeCheckoutSession

    const res = await apiMethod({
      planType: payload.plan,
      quantity: payload.quantity,
      origin: window.location.href,
      trackingData: mixpanelUtils.getProperties(),
    })
    if (res.sessionUrl) {
      window.location.href = res.sessionUrl
    }
  } catch (e) {
    if (e.body && e.body.message) {
      commit('createCheckoutSessionErrorMutation', e.body.message)
    }
    dispatch('hookBulkPricingAction', {
      planType: payload.plan,
      numOfLicenses: payload.quantity,
      email: payload.email,
      requirement: 'This was initiated because of failure during checkout session creation.',
    })
    throw e
  } finally {
    commit('isCreatingCheckoutSession', false)
  }
}
const createCheckoutSessionForPersonPlan = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)

    const { plan: planType, promotionCode, query } = payload
    commit('loadingPlan', planType)

    const data = await PricingApi.createCheckoutSessionForPersonPlan({
      planType,
      promotionCode,
      query,
      trackingData: mixpanelUtils.getProperties(),
    })
    if (data && data.sessionUrl) {
      window.location.href = data.sessionUrl
      commit('isCreatingCustomerPortal', false)
      commit('loadingPlan', null)
    }
  } catch (e) {
    Sentry.captureException(e)
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
    return e
  }
}
const createCustomerPortalUpgradeFlowAction = async ({ commit, rootGetters }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)

    const useV2Pricing = rootGetters['featureToggles/canUseNewBulkPricing']
    const apiMethod = useV2Pricing ?
      PricingApi.createCustomerPortalUpgradeFlowV2 :
      PricingApi.createCustomerPortalUpgradeFlow

    const data = await apiMethod(payload)
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
      commit('loadingPlan', null)
    }
  } catch (e) {
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
    Sentry.captureException(e)
    return e
  }
}
const createCustomerPortalUpgradeFlowV3Action = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)

    const data = await PricingApi.createCustomerPortalUpgradeFlowV3(payload)
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      commit('loadingPlan', null)
      window.location.href = data.url
    }
    if (data?.message) {
      utils.showToastSuccess(data.message)
    }
  } catch (e) {
    const errorMessage = e?.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    Sentry.captureException(e)
  } finally {
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
  }
}
const createCustomerPortalAction = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    const data = await PricingApi.createCustomerPortal({
      pathname: window.location.pathname,
      isUpdatingPlan: payload ? payload.isUpdatingPlan : false,
    })
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
    }
  } catch (e) {
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    commit('isCreatingCustomerPortal', false)
    Sentry.captureException(e)
  }
}
const hookBulkPricingAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookBulkPricing(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookBulkPricingErrorMutation', e)
  }
}
const getPromotionCode = async ({ commit }, payload) => {
  const { promotionCode } = await PromoApi.checkPromotion({
    promo: payload,
  })
  commit('setPromotionCode', promotionCode)
}

const setIsValidPromoCodeAction = async ({ commit }, payload) => {
  commit('setIsValidPromoCode', payload)
}
const updateRemoveCardPurposeAction = ({ commit }, payload) => {
  commit('setRemoveCardPurpose', payload)
}

const requestAdditionalLicensePopupAction = async ({ commit }, payload) => {
  commit('isShowingRequestAdditionalLicensePopup', payload)
}

const goToBulkLicensePageAction = () => {
  router.push({
    name: 'UserPlan',
    hash: '#bulk-pricing',
  })
}
const trackNewPurchaseAction = (
  { state }, payload,
) => {
  const { currentUser, plan } = state

  if (payload.query) {
    setTimeout(async () => {
      const res = await InvoiceApi.getInvoices()

      if (res.invoices && res.invoices.length === 0) {
        return
      }
      const invoice = res.invoices[0]

      const { source, type } = payload.query

      let coupon = ''
      if (invoice.coupon) {
        coupon = invoice.coupon.name
      }
      let discount = 0
      if (invoice.lines && invoice.lines.length > 0) {
        discount = invoice.lines[invoice.lines.length - 1].discountAmountRaw
      }
      const payment_method = invoice.paymentMethod.brand === 'visa' ? 'Stripe > Cards' : 'Stripe > iDEAL with SEPA'
      const params = {
        currency: invoice.currency,
        ahaslides_user_id: currentUser.id,
        transaction_id: invoice.invoiceId,
        value: invoice.amountPaidRaw,
        coupon,
        tax: 0,
        item_name: plan.name,
        discount,
        payment_method,
        item_category: plan.group,
        item_category2: plan.billingCycle,
        item_category3: type,
        item_variant: source === 'landing_site' ? 'Landing site' : 'Presenter app',
      }
      utils.trackGA4AndMixpanel('purchase', {
        ...params,
      })
      router.replace({ query: null })
    }, 1000)
  }
}

const hookCustomRequestAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookCustomRequest(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookCustomRequestError', e)
  }
}

const submitEnterpriseInquiriesFormAction = async (_, payload) => {
  try {
    return PricingApi.submitEnterpriseInquiriesForm(payload)
  } catch (e) {
    Sentry.captureException(e)
  }
}

const getMonthlyToYearlyUpgradePreviewAction = async ({ commit, state }) => {
  try {
    const currentPlan = state.currentUser.plan
    const upgradePlan = MONTHLY_YEARLY_UPGRADE_PLAN_MAP[currentPlan]
    if (!upgradePlan) {
      return
    }
    const res = await PricingApi.getMonthlyToYearlyUpgradePreview({
      plan: upgradePlan,
    })
    commit('setMonthlyToYearlyUpgradePreview', res)
  } catch (e) {
    Sentry.captureException(e)
    commit('setMonthlyToYearlyUpgradePreview', null)
  }
}

const upgradeFromMonthlyToYearlyAction = async ({
  getters, commit, dispatch, state,
}) => {
  try {
    if (state.isUpgradingFromMonthlyToYearly) {
      return
    }
    commit('setUpgradingFromMonthlyToYearly', true)
    const currentPlan = state.currentUser.plan
    const planToUpgrade = MONTHLY_YEARLY_UPGRADE_PLAN_MAP[currentPlan]
    if (!currentPlan || !planToUpgrade) {
      utils.showToastError(
        'The plan is not eligible for upgrade to yearly plan',
      )
      return
    }
    const payload = {
      plan: planToUpgrade,
    }
    if (getters.monthlyToYearlyUpgradePromotionCode) {
      payload.promotionCode = getters.monthlyToYearlyUpgradePromotionCode
    }
    await dispatch('createCustomerPortalUpgradeFlowAction', payload)
  } catch (e) {
    Sentry.captureException(e)
    utils.showToastError(
      'Something went wrong, please check your connection or contact us for support',
    )
  } finally {
    commit('setUpgradingFromMonthlyToYearly', false)
  }
}

const setShowMonthlyToYearlyPromoBannerAction = ({ commit }, payload) => {
  commit('setIsShowMonthlyToYearlyPromoBanner', payload)
}

const upgradePlanAction = async ({ state, dispatch }, payload) => {
  if (state.currentUser.plan === 'free') {
    await dispatch('createCheckoutSessionForPersonPlan', payload)
  } else {
    await dispatch('createCustomerPortalUpgradeFlowV3Action', payload)
  }
}

export default {
  createCheckoutSessionAction,
  createCheckoutSessionForPersonPlan,
  hookBulkPricingAction,
  updateRemoveCardPurposeAction,
  createCustomerPortalAction,
  createCustomerPortalUpgradeFlowAction,
  requestAdditionalLicensePopupAction,
  goToBulkLicensePageAction,
  trackNewPurchaseAction,
  hookCustomRequestAction,
  getPromotionCode,
  setIsValidPromoCodeAction,
  submitEnterpriseInquiriesFormAction,
  upgradeFromMonthlyToYearlyAction,
  getMonthlyToYearlyUpgradePreviewAction,
  setShowMonthlyToYearlyPromoBannerAction,
  upgradePlanAction,
  createCustomerPortalUpgradeFlowV3Action,
}
